import { css } from "@emotion/react";
import styled from "@emotion/styled";
import { FC } from "react";
import { FieldInputProps, FieldMetaState } from "react-final-form";

import { Info } from "@livechat/design-system-icons";
import {
  Button,
  DesignToken,
  FormField,
  Icon,
  Input,
  SpacingToken,
  Text,
  Tooltip,
  TransitionDurationToken,
  TransitionTimingToken,
} from "@livechat/design-system-react-components";

import Flex from "components/layout/Flex";
import { shouldDisplayError } from "helpers/final-form";
import { useToggle } from "hooks/use-toggle";
import { withVar } from "themes/helpers";

interface IProps {
  input: FieldInputProps<string, HTMLElement>;
  meta: FieldMetaState<string>;
  id: string;
  disabled: boolean;
}

export const CustomFieldApiKeyField: FC<IProps> = ({ input, meta, id, disabled }) => {
  const [isEditingApiKey, toggleEditingApiKey] = useToggle(false);

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const handleEditClick = () => {
    toggleEditingApiKey();
  };

  return (
    <FieldWrapper
      labelAdornment={
        <Tooltip placement="right" kind="invert" triggerRenderer={<Adornment source={Info} size="small" />}>
          Spaces and special characters aren&apos;t allowed. Once a field is created, the API key name can&apos;t be
          changed. Use the API key name to create custom fields with the API documentation.
        </Tooltip>
      }
      error={shouldDisplayError(meta)}
    >
      <Flex alignItems="center" gap={withVar(SpacingToken.Spacing1)} marginBottom={withVar(SpacingToken.Spacing1)}>
        <Text bold>API Key name</Text>
        <Tooltip placement="right" kind="invert" triggerRenderer={<Icon source={Info} size="small" />}>
          Spaces and special characters aren&apos;t allowed. Once a field is created, the API key name can&apos;t be
          changed. Use the API key name to create custom fields with the API documentation.
        </Tooltip>
        <Button kind="link" onClick={handleEditClick} data-testid={id} disabled={disabled} css={{ marginLeft: "auto" }}>
          {isEditingApiKey ? "Save" : "Edit"}
        </Button>
      </Flex>
      <ApiKeyInput
        id={id}
        isEditing={isEditingApiKey}
        disabled={!isEditingApiKey}
        error={shouldDisplayError(meta)}
        placeholder="Type display name first"
        {...input}
        data-1p-ignore
      />
    </FieldWrapper>
  );
};

const FieldWrapper = styled(FormField)`
  width: 100%;
  margin-top: var(${SpacingToken.Spacing6});
`;

const Adornment = styled(Icon)`
  margin-bottom: var(${SpacingToken.Spacing05});
`;

const ApiKeyInput = styled(Input)<{ isEditing: boolean }>`
  cursor: ${
    // FIXME: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type, @typescript-eslint/no-explicit-any
    ({ isEditing }: any) => (isEditing ? "text" : "pointer")
  };
  transition: all var(${TransitionDurationToken.Fast2}) var(${TransitionTimingToken.EaseOut});

  ${
    // FIXME: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type, @typescript-eslint/no-explicit-any
    ({ isEditing }: any) =>
      !isEditing &&
      css`
        border-color: transparent;
        background-color: transparent;
        padding: 5px 0;

        input:disabled {
          color: var(${DesignToken.ContentBasicPrimary});
        }

        &:hover,
        &:disabled,
        &:focus {
          border-color: transparent;
          color: var(${DesignToken.ContentBasicPrimary});
          background-color: transparent;
        }
      `
  }
`;
